import { BookOpen, Pencil, Trash2, Phone, MapPin, LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '@/utils/currency';
import type { ActionConfig, StatConfig } from '@/components/common/ContactCard';

interface CustomerListRowProps {
  name: string;
  subtitle?: string;
  isDefault?: boolean;
  phone?: string;
  postalCode?: string;
  address?: string;
  stats: StatConfig[];
  progressPercentage: number;
  progressLabel: string;
  progressSuffix?: string;
  actions: ActionConfig[];
  onClick?: () => void;
  onEdit?: () => void;
  onLedger?: () => void;
  onDelete?: () => void;
}

export default function CustomerListRow({
  name,
  subtitle,
  isDefault,
  phone,
  postalCode,
  address,
  stats,
  progressPercentage,
  progressLabel,
  progressSuffix = 'Paid',
  actions,
  onClick,
  onEdit,
  onLedger,
  onDelete,
}: CustomerListRowProps) {
  const { t } = useTranslation();
  const barWidth = Math.min(100, Math.max(0, progressPercentage));

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (!onClick) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="bg-white border border-gray-200 rounded-xl px-4 py-3 hover:border-primary-200 transition-colors cursor-pointer"
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-gray-900 truncate">{name}</h3>
                {isDefault && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                    {t('common.default')}
                  </span>
                )}
                {subtitle && <span className="text-xs text-gray-500">{subtitle}</span>}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                {phone && (
                  <span className="inline-flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" />
                    {phone}
                  </span>
                )}
                {postalCode && (
                  <span className="inline-flex items-center gap-1 font-medium text-gray-700">
                    <MapPin className="w-3.5 h-3.5" />
                    {postalCode}
                  </span>
                )}
                {address && !postalCode && (
                  <span className="inline-flex items-center gap-1 truncate max-w-xs">
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{address}</span>
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {onLedger && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLedger();
                  }}
                  className="p-1.5 rounded-lg text-indigo-600 bg-indigo-50 hover:bg-indigo-100"
                  title={t('common.ledger')}
                >
                  <BookOpen className="w-4 h-4" />
                </button>
              )}
              {onEdit && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  className="p-1.5 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200"
                  title={t('common.edit')}
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  className="p-1.5 rounded-lg text-red-600 bg-red-50 hover:bg-red-100 border border-red-200"
                  title={t('common.delete')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 lg:w-[340px] shrink-0">
          {stats.slice(0, 3).map((stat, idx) => {
            const StatIcon = stat.icon as LucideIcon;
            return (
              <div key={idx} className={`${stat.bgClass} rounded-lg px-2 py-1.5 text-center`}>
                <StatIcon className={`w-3.5 h-3.5 ${stat.iconColorClass} mx-auto mb-0.5`} />
                <p className="text-xs font-semibold text-gray-900 leading-tight">
                  {formatMoney(stat.value || 0)}
                </p>
                <p className="text-[10px] text-gray-500">{stat.label}</p>
              </div>
            );
          })}
        </div>

        <div className="lg:w-36 shrink-0">
          <div className="flex items-center justify-between text-[10px] mb-1 gap-1">
            <span className="text-gray-500 truncate">{progressLabel}</span>
            <span className="font-medium text-gray-900">
              {progressPercentage}% {progressSuffix}
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <div className="h-full rounded-full bg-blue-500" style={{ width: `${barWidth}%` }} />
          </div>
        </div>
      </div>

      {actions.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
          {actions.map((action, idx) => {
            const ActionIcon = action.icon;
            return (
              <button
                key={idx}
                type="button"
                title={action.label}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick();
                }}
                className={`px-2.5 py-1.5 text-xs font-medium ${action.textClass} ${action.bgClass} ${action.hoverClass} rounded-lg transition-colors inline-flex items-center gap-1.5`}
              >
                <ActionIcon className="w-3.5 h-3.5 shrink-0" />
                {action.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
